/**
 * @graphql agg
 */

import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import type { GraphQLContentStatus } from './__generated__/useAllCommentsDataQuery.graphql';
import type {
	useCommentByIdQuery$data,
	useCommentByIdQuery$variables,
} from './__generated__/useCommentByIdQuery.graphql';
import { useAllCommentsData, type Comment, type FetchPolicies } from './useAllCommentsData';
import { findCommentInData } from './commentDataManagerUtils';
// The `useCommentById` hook efficiently fetches a single comment by ID with a smart strategy that
// prioritizes fast loading: if the main "all comments" data query has loaded, it searches there
// for the specific comment.  If that query has not laoded - such as in the case of having a lot of
// comments on the page and thus a significant loading time, thenn it instead immediately makes a
// separate query for just the specific comment. This avoids waiting for the potentially slow "all
// comments" query to finish, ensuring quick access to individual comments.
// The hook also provides loading states, error handling, and comprehensive type safety.

// Unfortunately our Relay-friendly type generation tools don't support GQL fragments yet - but in
// order to keep the definition of the comment fields in sync (so the cache update fragment is
// always in sync with the full query) we will need to manually make any changes to one definition
// in any others as well, until our tooling supports fragments and then we move to a more
// fragmented-based approach.  Note there are also comment queries or fragments in the following
// files that need to be kept in sync as well:
// - next/packages/comments-data-manager/src/useAllCommentsData.ts
// - next/packages/comments-data-manager/src/updateCommentInCache.ts
// - next/packages/comments-data-manager/src/useCreateObjectCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineReplyMutation.ts

// TODO: Replace with the exported fragment from useAllCommentsData.ts when our tooling supports
// it, if we are allowed to share fragments between packages/files.  If we are not allowed to share
// fragments, we should move this hook - or at least the part that executes the query into the
// useAllCommentsData.ts file.
const commentByIdQuery = graphql`
	query useCommentByIdQuery($pageId: ID!, $commentId: ID!, $contentStatus: [GraphQLContentStatus]) {
		comments(pageId: $pageId, contentStatus: $contentStatus, commentId: $commentId) {
			nodes {
				id
				author {
					__typename
					displayName
					permissionType
					... on KnownUser {
						accountId
						permissionType
					}
					profilePicture {
						path
					}
					type
				}
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				container {
					id
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					editui
					webui
				}
				location {
					__typename
					type
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
					... on InlineComment {
						inlineMarkerRef
						inlineResolveProperties {
							isDangling
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
						inlineText
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
					ari
					containerAri
					reactionsCount
					reactionsSummaryForEmoji {
						id
						count
						emojiId
						reacted
					}
				}
				replies {
					id
					author {
						__typename
						displayName
						permissionType
						... on KnownUser {
							accountId
						}
						profilePicture {
							path
						}
					}
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						editui
						webui
					}
					location {
						__typename
						type
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
						... on InlineComment {
							inlineMarkerRef
							inlineResolveProperties {
								isDangling
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
							inlineText
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
						ari
						containerAri
						reactionsCount
						reactionsSummaryForEmoji {
							id
							count
							emojiId
							reacted
						}
					}
					version {
						by {
							__typename
							displayName
						}
						number
						when
					}
				}
				version {
					by {
						__typename
						type
						displayName
						... on KnownUser {
							accountId
						}
					}
					friendlyWhen
					number
					when
				}
			}
		}
	}
`;

export type UseCommentByIdOptions = {
	fetchPolicy?: FetchPolicies;
	onError?: (error: Error) => void;
	skip?: boolean;
};

export type UseCommentByIdProps = {
	commentId: string;
	contentId: string;
	contentStatus?: GraphQLContentStatus[];
	options?: UseCommentByIdOptions;
};

export type UseCommentByIdReturn = {
	loading: boolean;
	error: Error | undefined;
	comment: Comment | undefined;
};

/**
 * Error message used when a comment is not found after all data has loaded.
 * NOTE: This currently matches what the backend returns, but is not guaranteed to match in the future.
 * There is a separate case for this string in errorUtils, but it still risks getting out of date if used elsewhere.
 */
export const USE_COMMENT_BY_ID_COMMENT_NOT_FOUND_MESSAGE =
	'Comment not found: No comment found for the given id';

/**
 * Custom React hook for loading a single comment by ID
 *
 * Features:
 * - First tries to find the comment in the data from useAllCommentsData
 * - If not found (either because useAllCommentsData is loading or comment doesn't exist there),
 *   immediately makes a separate query for the specific comment
 * - Does not wait for useAllCommentsData to complete - prioritizes fast individual comment loading
 * - Provides loading states and error handling
 * - Includes comprehensive type safety
 *
 * @param commentId - The ID of the comment to fetch
 * @param options - Configuration options for the hook
 * @returns Object containing comment data, loading state, and utility functions
 */
export const useCommentById = ({
	commentId,
	contentId,
	contentStatus = ['CURRENT'],
	options = {},
}: UseCommentByIdProps): UseCommentByIdReturn => {
	const { fetchPolicy = 'cache-first', onError, skip = false } = options;

	// First, try to get the comment from the all comments data
	const skipQueries = skip || !contentId || !commentId;
	const allCommentsData = useAllCommentsData({
		contentId,
		contentStatus,
		options: {
			fetchPolicy: 'cache-only',
			// (If the caller is requesting that we definitely fetch the comment, then we don't care if
			//  it is in the cached data)
			skip: skipQueries || fetchPolicy === 'network-only',
		},
	});

	// If the all comment data hasn't loaded yet (which could be a long time if there are a lot
	// of comments), then query for just this one comment
	const oneCommentData = useQueryInterop<useCommentByIdQuery$data, useCommentByIdQuery$variables>(
		commentByIdQuery,
		{
			variables: {
				pageId: contentId ?? '',
				commentId,
				contentStatus,
			},
			fetchPolicy,
			// skip if all comments data has loaded - but always fetch if fetchPolicy is network-only
			skip: (skipQueries || !allCommentsData.loading) && fetchPolicy !== 'network-only',
			onError: (error: Error) => {
				// Handle specific GraphQL errors gracefully
				// WS-3494 - If the error isn't related to reactionsSummary returning null, just mark it handled
				// COMMENTS-188 - Or if the error isn't related to cc-pages not being reachable just mark it handled
				if (
					error?.message?.search(/reactions/i) !== -1 ||
					error?.message?.search(/cc-pages/i) !== -1
				) {
					markErrorAsHandled(error);
				}
				onError?.(error);
			},
		},
	);

	// Look for the comment in the whichever comment data has loaded
	// (Use filtered comments so we're only seeing what useQuery would see)
	const finalComment = useMemo(() => {
		if (!contentId || !commentId || !(allCommentsData.allFilteredComments || oneCommentData.data)) {
			return undefined;
		}

		let commentData: Comment[] = [];
		if (allCommentsData.allFilteredComments.length > 0) {
			commentData = allCommentsData.allFilteredComments;
		} else if (oneCommentData.data?.comments?.nodes) {
			// Filter out any null nodes - can't just use .filter because TS complains about type
			// mismatch.  Fortunately, this query should only have one comment node anyway.
			commentData = oneCommentData.data?.comments?.nodes
				?.map((node) => node)
				.filter((node) => node);
		}

		// Search in retreived comments and their replies
		return findCommentInData(commentData, commentId);
	}, [allCommentsData.allFilteredComments, oneCommentData.data, contentId, commentId]);

	// Create an error if we have the full response but can't find the comment
	const commentNotFoundError = useMemo(() => {
		// Only create error if:
		// 1. We're not loading anymore (have full response)
		// 2. We have data (either from allCommentsData or oneCommentData)
		// 3. We searched for the comment but didn't find it
		// 4. There's no existing error
		if (
			!skipQueries &&
			!allCommentsData.loading &&
			allCommentsData.isDataProcessed &&
			!oneCommentData.loading &&
			!allCommentsData.error &&
			!oneCommentData.error &&
			(allCommentsData.allFilteredComments || oneCommentData.data) &&
			finalComment === undefined
		) {
			return new Error(USE_COMMENT_BY_ID_COMMENT_NOT_FOUND_MESSAGE);
		}
		return undefined;
	}, [
		skipQueries,
		allCommentsData.loading,
		allCommentsData.isDataProcessed,
		allCommentsData.error,
		allCommentsData.allFilteredComments,
		oneCommentData.loading,
		oneCommentData.error,
		oneCommentData.data,
		finalComment,
	]);

	return {
		loading: allCommentsData.loading && oneCommentData.loading, // (Only loading state if both are loading)
		error: allCommentsData.error || oneCommentData.error || commentNotFoundError,
		comment: finalComment,
	};
};

export type FetchCommentByIdProps = {
	commentId?: string;
	contentId: string;
	fetchPolicy?: 'cache-first' | 'network-only' | 'cache-only' | 'no-cache';
};

/**
 * Fetches a single comment by ID by executing the GraphQL query.
 * This function can be used for preloading comment data outside of React components.
 *
 * @param commentId - The ID of the comment to fetch
 * @param contentId - The ID of the content/page
 * @param fetchPolicy - Optional fetch policy (defaults to 'cache-first')
 * @returns Promise that resolves to the comment, or null if not found
 */
export const fetchCommentById = async ({
	commentId,
	contentId,
	fetchPolicy = 'cache-first',
}: FetchCommentByIdProps): Promise<Comment | null> => {
	if (!contentId || !commentId) {
		return Promise.resolve(null);
	}

	const response = await queryInterop<useCommentByIdQuery$data>({
		query: commentByIdQuery,
		variables: {
			pageId: contentId,
			commentId: commentId || '',
			// Query for all content statuses - don't filter by contentStatus
			contentStatus: ['DRAFT', 'CURRENT', 'ARCHIVED', 'DELETED'],
		},
		fetchPolicy,
	});

	const nodes = response?.data?.comments?.nodes;
	if (!nodes || nodes.length === 0) {
		return null;
	}

	// Filter out null nodes and use findCommentInData to search through comments and replies
	const filteredNodes = nodes.filter((node): node is NonNullable<typeof node> => node !== null);
	return findCommentInData(filteredNodes, commentId) ?? null;
};
